import { addNotification, clearNotificationsForPet, type AppNotification } from './notificationService';
import { formatTime } from './timeUtils';
import type { Medication } from '../types';
import {
  loadReminderPreferences,
  shouldFireLogReminder,
  shouldFireMedReminder,
  type ReminderPreferences,
} from './reminderScheduler';

interface ReminderState {
  medications: Medication[];
  petId: string | null;
  petName: string | null;
}

const LOOP_INTERVAL_MS = 60_000;
const FIRE_STORE_KEY = 'koda_fired_med_times';
const FIRE_STORE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const TIME_WINDOW_MINUTES = 5;

let reminderState: ReminderState = { medications: [], petId: null, petName: null };
let loopId: number | null = null;
let visibilityHandler: (() => void) | null = null;
let onlineHandler: (() => void) | null = null;
let storageHandler: (() => void) | null = null;

function loadFiredStore(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(FIRE_STORE_KEY) || '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

function saveFiredStore(store: Record<string, number>): void {
  localStorage.setItem(FIRE_STORE_KEY, JSON.stringify(store));
}

function pruneFiredStore(now = Date.now()): void {
  const store = loadFiredStore();
  let changed = false;

  for (const [key, firedAt] of Object.entries(store)) {
    if (!Number.isFinite(firedAt) || now - firedAt > FIRE_STORE_TTL_MS) {
      delete store[key];
      changed = true;
    }
  }

  if (changed) {
    saveFiredStore(store);
  }
}

function hasFired(key: string): boolean {
  const store = loadFiredStore();
  return Boolean(store[key]);
}

function markFired(key: string): void {
  const store = loadFiredStore();
  store[key] = Date.now();
  saveFiredStore(store);
}

function clearFiredStoreForPet(petId: string): void {
  if (!petId) return;
  const store = loadFiredStore();
  const token = `:${petId}:`;
  let changed = false;

  for (const key of Object.keys(store)) {
    if (key.includes(token)) {
      delete store[key];
      changed = true;
    }
  }

  if (changed) {
    saveFiredStore(store);
  }
}

function supportsBrowserNotifications(): boolean {
  return typeof window !== 'undefined' && typeof Notification !== 'undefined';
}

function canUseBrowserNotification(): boolean {
  return supportsBrowserNotifications() && Notification.permission === 'granted';
}

function formatMedicationLabel(medication: Medication): string {
  const dosage = medication.dosage_mg?.trim();
  return dosage ? `${medication.name} (${dosage})` : medication.name;
}

function buildMedicationNotification(medications: Medication[], timeslot: string, preferences: ReminderPreferences, petName: string | null): Omit<AppNotification, 'id' | 'timestamp'> {
  const labels = medications.map(formatMedicationLabel).filter(Boolean);
  const petPrefix = petName ? `${petName} — ` : '';
  const body = labels.length > 1
    ? `${petPrefix}Time to give: ${labels.join(', ')}`
    : `${petPrefix}Time to give ${labels[0] || 'medication'}`;

  return {
    kind: 'med',
    title: 'Medication reminder',
    body,
    rawBody: labels.join(', '),
    time: formatTime(timeslot, Boolean(preferences.use24h)),
    read: false,
    petName: petName ?? undefined,
  };
}

function buildDailyLogNotification(preferences: ReminderPreferences, petName: string | null): Omit<AppNotification, 'id' | 'timestamp'> {
  const petPrefix = petName ? `${petName} — ` : '';
  return {
    kind: 'reminder',
    title: 'Daily log reminder',
    body: `${petPrefix}Time to complete today's daily log.`,
    time: preferences.logReminderAM ? formatTime(preferences.logReminderAM, Boolean(preferences.use24h)) : '',
    read: false,
    petName: petName ?? undefined,
  };
}

function createReminderKey(kind: 'med' | 'log', petId: string | null, dateKey: string, value: string): string {
  return [kind, petId || 'default', dateKey, value].join(':');
}

function fireBrowserNotification(notification: Omit<AppNotification, 'id' | 'timestamp'>): void {
  if (!canUseBrowserNotification()) return;
  try {
    new Notification(notification.title, {
      body: notification.body,
      tag: `${notification.kind}:${notification.petId ?? 'default'}:${notification.time}`,
    });
  } catch {
    // Non-fatal. The in-app notification still exists.
  }
}

function runReminderCheck(): void {
  if (typeof window === 'undefined') return;
  pruneFiredStore();

  const preferences = loadReminderPreferences();
  const now = new Date();
  const dateKey = now.toISOString().slice(0, 10);

  if (shouldFireLogReminder(preferences, now)) {
    const logKey = createReminderKey('log', reminderState.petId, dateKey, preferences.logReminderAM || '');
    if (!hasFired(logKey)) {
      const notification = buildDailyLogNotification(preferences, reminderState.petName);
      const created = addNotification({
        id: `reminder:${logKey}`,
        ...notification,
        petId: reminderState.petId ?? undefined,
      });
      fireBrowserNotification(created);
      markFired(logKey);
    }
  }

  const dueByTime = new Map<string, Medication[]>();

  for (const medication of reminderState.medications) {
    if (!shouldFireMedReminder(medication, preferences, now)) continue;

    for (const timeslot of medication.timeslots) {
      const [hours, minutes] = timeslot.trim().split(':').map(Number);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) continue;

      const scheduled = new Date(now);
      scheduled.setHours(hours, minutes, 0, 0);
      const deltaMinutes = Math.abs(now.getTime() - scheduled.getTime()) / 60000;
      if (deltaMinutes > TIME_WINDOW_MINUTES) continue;

      const key = createReminderKey('med', reminderState.petId, dateKey, timeslot);
      if (hasFired(key)) continue;

      const existing = dueByTime.get(timeslot) || [];
      existing.push(medication);
      dueByTime.set(timeslot, existing);
    }
  }

  for (const [timeslot, medications] of dueByTime.entries()) {
    const key = createReminderKey('med', reminderState.petId, dateKey, timeslot);
    if (hasFired(key)) continue;

    const notification = buildMedicationNotification(medications, timeslot, preferences, reminderState.petName);
    const created = addNotification({
      id: `reminder:${key}`,
      ...notification,
      petId: reminderState.petId ?? undefined,
    });
    fireBrowserNotification(created);
    markFired(key);
  }
}

function attachListeners(): void {
  if (typeof window === 'undefined') return;

  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      runReminderCheck();
    }
  };

  onlineHandler = () => runReminderCheck();
  storageHandler = () => runReminderCheck();

  document.addEventListener('visibilitychange', visibilityHandler);
  window.addEventListener('online', onlineHandler);
  window.addEventListener('storage', storageHandler);
}

function detachListeners(): void {
  if (typeof window === 'undefined') return;
  if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
  if (onlineHandler) window.removeEventListener('online', onlineHandler);
  if (storageHandler) window.removeEventListener('storage', storageHandler);
  visibilityHandler = null;
  onlineHandler = null;
  storageHandler = null;
}

export function startReminderLoop(state: ReminderState): void {
  reminderState = state;

  if (loopId !== null) {
    return;
  }

  runReminderCheck();
  attachListeners();
  loopId = window.setInterval(runReminderCheck, LOOP_INTERVAL_MS);
}

export function updateReminderState(state: ReminderState): void {
  reminderState = state;
}

export function stopReminderLoop(): void {
  if (loopId !== null) {
    window.clearInterval(loopId);
    loopId = null;
  }
  detachListeners();
}

export function runReminderCheckNow(): void {
  runReminderCheck();
}

export function clearReminderDataForPet(petId: string): void {
  if (!petId) return;
  clearNotificationsForPet(petId);
  clearFiredStoreForPet(petId);
}
